/**
 * Phase B — `IdeaWorkspace.tsx` 本実装 (旧 `old/apps/user/src/components/ecw/IdeaForm.tsx` 1364 行を v2 風に再構築)。
 *
 * 3 ペイン構造 (Tailwind grid、desktop) + Step 0..6 の wizard 進行 + 3 callable (step 対話 / タイトル / Insight 要約)。
 * AnalysisPage が `screen='ideaForm'` のときに丸ごと差し替える **full-screen component** (旧の modal ではない)。
 *
 *   - `mode: 'front'` (Step 1-3 + Step 4 タイトル = 気づき作成、`selectedPosts` 必須)
 *   - `mode: 'back'`  (Step 4-6 = 処方箋作成、`selectedInsights` 必須、Step3 で AI step3 自動発動)
 *
 * 旧との細部踏襲:
 *   - 左ペイン (mini-map 50% + post/insight カード 50%、カードクリックで inline 詳細表示)
 *   - 中央ペイン (wizard-progress ドット + Step ごとに AI message box + textarea + 次へ button)
 *   - 右ペイン (aiReferences 参考事例 grid、step3 後は `llmStep3.references` 差し替え、
 *     「処方に追加」「効能に追加」で textarea に append)
 *   - canGoNext / handleNextStep の AI 呼出トリガ / handleSave 重複 title `_2` 付加 等を 1:1 踏襲
 *   - 楽観 UI (即時 saveMessage toast 1500ms → showSaveCompleteDialog)
 *
 * 地図キャプチャ (旧 STEP 6 の「🗺 地図をキャプチャ」) と PDF 出力は scope 外 (TODO コメント)。
 */

import type {
  AiCategoryMasterRow,
  AiDialogueSettings,
  AiReference,
  EcwIdeaReference,
  FieldDef,
} from '@citizen-walk/shared-types';
import { Button, useToast } from '@citizen-walk/shared-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ECW_EMOTION_DESCRIPTIONS, safeHttpUrl } from '@citizen-walk/shared-types';
import { aiCallableErrorMessage } from './aiCallableErrorMessage';
import type { PostMinimal } from '../posts';
import { PostDetailModal, usePhotoUrl } from '../posts';
import {
  getDialogueStepEnabledMap,
  getDialogueStepPrompt,
  loadAiDialogueSettings,
} from '../analysis';
import { IdeaWorkspaceMiniMap, type IdeaWorkspaceMiniMapHandle } from './IdeaWorkspaceMiniMap';
import {
  COLOR_PALETTE,
  aiReferenceToDisplay,
  buildCategoryMasterBlock,
  buildQuestionsBlock,
  buildReferencesBlock,
  computeAverageSentiment,
  ensureUniqueIdeaTitle,
  formatDisplayName,
  formatPostsAsFacts,
} from './ideaFormHelpers';
import { sentimentToHsl } from './sentimentColor';
import { useAiReferences } from './useAiReferences';
import type { CreateIdeaInput } from './useEcwIdeas';
import type { CreateInsightInput, InsightMinimal } from './useEcwInsights';
import {
  useGenerateEcwDialogue,
  useGenerateEcwInsightSummary,
  useGenerateEcwTitleSuggestions,
  type EcwStep1Result,
  type EcwStep2Result,
  type EcwStep3Result,
  type EcwStep4Result,
} from './useGenerateEcwDialogue';

/* ============================================================================
 * Props
 * ========================================================================== */

export type IdeaWorkspaceMode = 'front' | 'back';

export interface IdeaWorkspaceProps {
  districtId: string;
  /** 'front' = Step1-3 + Step4 タイトル (気づき作成)、'back' = Step4-6 (処方箋作成)。 */
  mode: IdeaWorkspaceMode;
  uid: string | null;
  /** R-70: 「自分の投稿」とみなす全 uid (現 uid + 端末引き継ぎ元)。投稿詳細の owner 判定に渡す。 */
  selfUids?: string[];
  /** 地区の M5 `analysisDisplayConfigId`。投稿詳細を投稿アプリと同じ表示内容にするため渡す。 */
  analysisDisplayConfigId?: string | null;
  /** 表示用 nickname (`${nickname}（${ageGroup}/${gender}）` 形式) — 親側で formatDisplayName 済。 */
  nickname: string;
  /** 旧の userSettings.ageGroup / gender (display name 用)。任意。 */
  ageGroup?: string;
  gender?: string;
  /** 起動時に選ばれた posts (front=必須、back=任意で関連 posts)。 */
  selectedPosts: PostMinimal[];
  /**
   * 地区の投稿項目定義 (`dataSettings.postFields`)。{{FACTS}} に「投稿者が入力した項目すべて」を
   * 地区ごとのラベル付きで載せるために使う。未指定ならタイトル / 位置 / コメントのみ。
   */
  postFields?: FieldDef[];
  /**
   * M2 分類マスタの コード → 行 map (`useAiCategoryMasters().byCategoryCd`)。
   * {{CATEGORY_MASTER}} の「出現コードだけの対応表」組立に使う。未指定なら対応表を送らない。
   */
  categoryMasterRows?: ReadonlyMap<string, AiCategoryMasterRow>;
  /** 起動時 context: Plutchik 8 感情のいずれか。 */
  emotion: string;
  /** 起動時 context: categoryMaster の表示名。 */
  categoryLabel: string;
  /** 後半モード時の選択 insights (back 必須)。 */
  selectedInsights?: InsightMinimal[];
  /** 既存 idea 一覧 (back モード時の title 重複検出に使う)。 */
  allIdeaTitles?: string[];
  /** M4 aiDialogueSettings id (未指定なら callable 側で fallback)。 */
  aiDialogueSettingsId?: string | null;
  /**
   * docs/107 Wave5: 分析(詳細選択) でアクティブだった絞込フィルタの文字列リスト
   * (old2 onIdeaGo の filterList)。front mode で「どの軸で選んだ投稿か」を文脈表示する。
   */
  filterContext?: string[];
  /** 戻る (Analysis 画面に戻る) ボタン。 */
  onBack: () => void;
  /** Insight 作成 (front mode で呼ばれる)。 */
  onCreateInsight: (input: CreateInsightInput) => Promise<void>;
  /** Idea 作成 (back mode で呼ばれる)。 */
  onCreateIdea: (input: CreateIdeaInput) => Promise<void>;
  /** 保存後の遷移先タブを親に通知 (Insight: 'insights' | 'matrix' | 'map'、Idea: 'insights' | 'ideas')。 */
  onSavedAndNavigate: (targetTab: string) => void;
}

/* ============================================================================
 * Main component
 * ========================================================================== */

const DEFAULT_STEP0_QUESTION =
  'まず最初に、まちを歩いたり、選択した写真を見たりして、どのような感想を持ったか、どのようなことを考えたいかなどを教えてください。';

export function IdeaWorkspace({
  districtId,
  mode,
  uid,
  selfUids,
  analysisDisplayConfigId,
  nickname,
  ageGroup,
  gender,
  selectedPosts,
  postFields,
  categoryMasterRows,
  emotion,
  categoryLabel,
  selectedInsights = [],
  allIdeaTitles = [],
  aiDialogueSettingsId,
  filterContext = [],
  onBack,
  onCreateInsight,
  onCreateIdea,
  onSavedAndNavigate,
}: IdeaWorkspaceProps) {
  /* ---------- step state ---------- */
  const startStep = mode === 'back' ? 4 : 1;
  const [currentStep, setCurrentStep] = useState<number>(startStep);

  /* ---------- M4 対話設定 (Step0 質問文 / stepEnabled) ----------
   * docs/107 Wave9 / old2 IdeaForm L84-85,191-198: aiDialogueSettingsId から M4 master を
   * 読み出し、Step0 のブロッキング質問文 + 各 step の enabled flag を駆動する。
   * 未指定 / fetch 失敗時は null のまま → 既定動作 (Step1 即時自動発動) で継続。 */
  const [dialogueConfig, setDialogueConfig] = useState<AiDialogueSettings | null>(null);
  const [dialogueConfigLoaded, setDialogueConfigLoaded] = useState(false);

  /* ---------- ユーザー入力 state ---------- */
  const [initialThoughts, setInitialThoughts] = useState('');
  const [observations, setObservations] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [actionIdea, setActionIdea] = useState('');
  const [expectedEffects, setExpectedEffects] = useState('');
  const [title, setTitle] = useState('');
  const [insightTitle, setInsightTitle] = useState('');
  const [referenceImageBase64, setReferenceImageBase64] = useState('');

  /* ---------- AI 出力 (callable から取得した step 結果) ---------- */
  const [llmStep1, setLlmStep1] = useState<EcwStep1Result | null>(null);
  const [llmStep2, setLlmStep2] = useState<EcwStep2Result | null>(null);
  const [llmStep3, setLlmStep3] = useState<EcwStep3Result | null>(null);
  const [llmStep4, setLlmStep4] = useState<EcwStep4Result | null>(null);
  const [titleSuggestions, setTitleSuggestions] = useState<string[]>([]);
  const [insightTitleSuggestions, setInsightTitleSuggestions] = useState<string[]>([]);
  const [insightSummary, setInsightSummary] = useState('');

  /* ---------- UI state ---------- */
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [showSaveCompleteDialog, setShowSaveCompleteDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState<PostMinimal | null>(null);
  const [selectedInsightDisplay, setSelectedInsightDisplay] = useState<InsightMinimal | null>(null);
  const [showFactPane, setShowFactPane] = useState(false);
  const [showRefPane, setShowRefPane] = useState(false);
  /* docs/107 Wave9 / old2 IdeaForm L112,117,344: タイトル再生成の loading + Step0 完了 flag。 */
  const [loadingInsightTitles, setLoadingInsightTitles] = useState(false);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [step0Done, setStep0Done] = useState(false);

  /* ---------- AI 対話に渡す投稿データ ({{FACTS}} / {{CATEGORY_MASTER}}) ----------
   * 既定 (M4 対話設定に factsConfig が無い地区) は従来どおり
   * 「投稿者が入力した項目すべて (地区の postFields 駆動) + AI 生成タイトル + 分類コード」+
   * 「facts に出現した分類コードだけ」の対応表。
   * M4 の `factsConfig` があればその構成 (AI の所見テキスト追加 / 大量選択時の縮小) に従う。
   * step1/2/3 で共用するため memo 化 (自動 Step1 は dialogueConfigLoaded を待つので取りこぼさない)。 */
  const factsConfig = dialogueConfig?.factsConfig ?? null;
  const facts = useMemo(
    () => formatPostsAsFacts(selectedPosts, postFields, factsConfig),
    [selectedPosts, postFields, factsConfig],
  );
  const categoryMaster = useMemo(
    () => buildCategoryMasterBlock(selectedPosts, categoryMasterRows, factsConfig),
    [selectedPosts, categoryMasterRows, factsConfig],
  );

  /* ---------- AI hooks ---------- */
  const { generate: generateDialogueRaw } = useGenerateEcwDialogue();
  const { generate: generateTitlesRaw } = useGenerateEcwTitleSuggestions();
  const { generate: generateSummaryRaw } = useGenerateEcwInsightSummary();
  // AI 生成の callable が失敗したら理由を知らせる (権限・1 日の上限などは Functions が日本語の
  // 理由を返す)。呼出側の既存の後始末 (try/finally) はそのままにするため、知らせたうえで投げ直す。
  const { toast } = useToast();
  const notifyAiError = useCallback(
    (e: unknown) => toast('error', aiCallableErrorMessage(e)),
    [toast],
  );
  const generateDialogue = useCallback(
    async (input: Parameters<typeof generateDialogueRaw>[0]) => {
      try {
        return await generateDialogueRaw(input);
      } catch (e) {
        notifyAiError(e);
        throw e;
      }
    },
    [generateDialogueRaw, notifyAiError],
  );
  const generateTitles = useCallback(
    async (input: Parameters<typeof generateTitlesRaw>[0]) => {
      try {
        return await generateTitlesRaw(input);
      } catch (e) {
        notifyAiError(e);
        throw e;
      }
    },
    [generateTitlesRaw, notifyAiError],
  );
  const generateSummary = useCallback(
    async (input: Parameters<typeof generateSummaryRaw>[0]) => {
      try {
        return await generateSummaryRaw(input);
      } catch (e) {
        notifyAiError(e);
        throw e;
      }
    },
    [generateSummaryRaw, notifyAiError],
  );

  /* ---------- M4 対話設定ロード (old2 IdeaForm L191-198) ---------- */
  useEffect(() => {
    let cancelled = false;
    loadAiDialogueSettings(aiDialogueSettingsId)
      .then((res) => {
        if (cancelled) return;
        setDialogueConfig(res.settings);
        setDialogueConfigLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('[IdeaWorkspace] loadAiDialogueSettings error', e);
        setDialogueConfigLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [aiDialogueSettingsId]);

  /* stepEnabled map (未設定 step は true 扱い、old2 `stepEnabled.stepN !== false`)。 */
  const stepEnabledMap = useMemo(() => getDialogueStepEnabledMap(dialogueConfig), [dialogueConfig]);
  const isStepEnabled = useCallback(
    (stepId: string): boolean => stepEnabledMap[stepId] !== false,
    [stepEnabledMap],
  );
  /* Step0 質問文 (M4 駆動、未取得は既定文)。 */
  const step0Question = useMemo(
    () => getDialogueStepPrompt(dialogueConfig, 'step0') ?? DEFAULT_STEP0_QUESTION,
    [dialogueConfig],
  );
  /* Step0 ブロッキング表示判定 (old2 L347-351): front + M4 で step0 有効 + 未完。 */
  const shouldShowStep0 =
    mode === 'front' && dialogueConfig !== null && isStepEnabled('step0') && !step0Done;

  /* ---------- mini-map ref (Phase B+1: Step 6 地図キャプチャ用) ---------- */
  const miniMapRef = useRef<IdeaWorkspaceMiniMapHandle>(null);
  const [capturingMap, setCapturingMap] = useState(false);

  const handleCaptureMap = useCallback(async () => {
    if (!miniMapRef.current || capturingMap) return;
    setCapturingMap(true);
    try {
      const dataUrl = await miniMapRef.current.capture();
      if (dataUrl) {
        setReferenceImageBase64(dataUrl);
      } else {
        window.alert('地図のキャプチャに失敗しました。少し時間をおいて再度お試しください。');
      }
    } finally {
      setCapturingMap(false);
    }
  }, [capturingMap]);

  /* ---------- aiReferences (右ペイン) ---------- */
  const refsState = useAiReferences([emotion]);
  const aiRefs: AiReference[] = refsState.status === 'ready' ? refsState.items : [];

  /* ---------- back モード起動時の prefill + AI step3 自動発動 + summary 生成 ---------- */
  const backInitRef = useRef(false);
  useEffect(() => {
    if (mode !== 'back' || backInitRef.current) return;
    if (selectedInsights.length === 0) return;
    backInitRef.current = true;
    // 1. observations / diagnosis を join して prefill (旧 L132-137)
    const obs = selectedInsights
      .map((i) => i.observations)
      .filter(Boolean)
      .join('\n');
    const dia = selectedInsights
      .map((i) => i.diagnosis)
      .filter(Boolean)
      .join('\n');
    setObservations(obs);
    setDiagnosis(dia);

    // 2. Insight summary (callable)
    // 旧 IdeaForm L140-143 踏襲: emotion / category も含めて要約精度を上げる。
    const insightTexts = selectedInsights.map(
      (i) =>
        `感情: ${i.emotionTag ?? ''}, カテゴリ: ${i.categoryLabel ?? ''}, 症状: ${i.observations}, 診断: ${i.diagnosis}`,
    );
    generateSummary({ districtId, insightTexts })
      .then((res) => {
        if (res.ok) setInsightSummary(res.summary);
      })
      .catch(() => {
        /* swallow */
      });
  }, [mode, selectedInsights, districtId, generateSummary]);

  /* ---------- back モード Step3 (AI 自動) ---------- */
  const step3AutoRef = useRef(false);
  useEffect(() => {
    if (mode !== 'back' || step3AutoRef.current || llmStep3 || !observations || !diagnosis) {
      return;
    }
    // step3 が無効なら自動発動しない (old2 L270)
    if (!isStepEnabled('step3')) return;
    step3AutoRef.current = true;
    setGenerating(true);
    void (async () => {
      try {
        const res = await generateDialogue({
          step: 'step3',
          districtId,
          aiDialogueSettingsId: aiDialogueSettingsId ?? undefined,
          facts,
          categoryMaster,
          emotion,
          categoryLabel,
          userObservations: observations,
          userDiagnosis: diagnosis,
          questions: buildQuestionsBlock(aiRefs),
          references: buildReferencesBlock(aiRefs),
        });
        if (res.ok && res.step === 'step3') setLlmStep3(res.result);
      } finally {
        setGenerating(false);
      }
    })();
  }, [
    mode,
    llmStep3,
    observations,
    diagnosis,
    districtId,
    emotion,
    categoryLabel,
    aiDialogueSettingsId,
    aiRefs,
    facts,
    categoryMaster,
    isStepEnabled,
    generateDialogue,
  ]);

  /* ---------- front モード Step1 生成 (old2 handleGenerateLLM L201-229) ----------
   * Step1 が無効ならAI呼び出しせず Step1 入力欄だけ出す。initialThoughts を {{USER_INITIAL_THOUGHTS}}
   * として渡す。「最初からやり直す」/ Step0 submit / 自動発動 から共用。 */
  const front1AutoRef = useRef(false);
  const runStep1 = useCallback(async () => {
    if (selectedPosts.length === 0) {
      window.alert('分析する市民データが選択されていません。');
      return;
    }
    setCurrentStep(1);
    // Step1 が無効ならAI呼び出しなしで Step1 入力欄に進む (old2 L207-210)
    if (!isStepEnabled('step1')) return;
    setGenerating(true);
    try {
      const res = await generateDialogue({
        step: 'step1',
        districtId,
        aiDialogueSettingsId: aiDialogueSettingsId ?? undefined,
        facts,
        categoryMaster,
        emotion,
        categoryLabel,
        questions: buildQuestionsBlock(aiRefs),
        userInitialThoughts: initialThoughts,
      });
      if (res.ok && res.step === 'step1') setLlmStep1(res.result);
    } finally {
      setGenerating(false);
    }
  }, [
    selectedPosts,
    facts,
    categoryMaster,
    isStepEnabled,
    districtId,
    aiDialogueSettingsId,
    emotion,
    categoryLabel,
    aiRefs,
    initialThoughts,
    generateDialogue,
  ]);

  /* 起動時 自動 Step1 (old2 L330-341): 設定読込完了を待ち、Step0 が無効な場合のみ即時発動。
   * Step0 が有効ならユーザーが Step0 submit するまで待つ。 */
  useEffect(() => {
    if (mode !== 'front' || front1AutoRef.current) return;
    if (selectedPosts.length === 0) return;
    // M4 設定読込完了 + aiReferences 読込完了を待つ
    if (!dialogueConfigLoaded || refsState.status !== 'ready') return;
    // Step0 が有効ならユーザー入力後に実行 (ここでは発動しない)
    if (isStepEnabled('step0') && dialogueConfig !== null) return;
    front1AutoRef.current = true;
    void runStep1();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedPosts, refsState.status, dialogueConfigLoaded]);

  /* ---------- Step0 submit (old2 handleStep0Submit L354-359) ---------- */
  const handleStep0Submit = useCallback(() => {
    if (front1AutoRef.current) return;
    setStep0Done(true);
    front1AutoRef.current = true;
    void runStep1();
  }, [runStep1]);

  /* ---------- 「最初からやり直す」 (old2 header button → handleGenerateLLM) ----------
   * Step1 から AI を再生成。Step2 以降の入力 / AI 出力もクリアして一からやり直す。 */
  const handleRestart = useCallback(() => {
    if (generating) return;
    setObservations('');
    setDiagnosis('');
    setInsightTitle('');
    setInsightTitleSuggestions([]);
    setLlmStep1(null);
    setLlmStep2(null);
    front1AutoRef.current = true;
    void runStep1();
  }, [generating, runStep1]);

  /* ---------- canGoNext (旧 L150-158) ---------- */
  const canGoNext = useCallback((): boolean => {
    if (currentStep === 1) return true;
    if (currentStep === 2) return observations.trim().length > 0;
    if (currentStep === 3) return diagnosis.trim().length > 0;
    if (currentStep === 4) {
      if (mode === 'front') return insightTitle.trim().length > 0;
      return actionIdea.trim().length > 0;
    }
    if (currentStep === 5) return expectedEffects.trim().length > 0;
    if (currentStep === 6) return title.trim().length > 0;
    return false;
  }, [
    currentStep,
    observations,
    diagnosis,
    actionIdea,
    insightTitle,
    expectedEffects,
    title,
    mode,
  ]);

  /* ---------- handleNextStep (旧 L232-326) ---------- */
  const handleNextStep = useCallback(async () => {
    if (!canGoNext() || generating) return;
    let nextStep = currentStep;

    try {
      if (currentStep === 1) {
        nextStep = 2;
      } else if (currentStep === 2) {
        // Step2: AI step2 (diagnosisQuestions) を取得 (step2 無効ならスキップ、old2 L242)
        if (!llmStep2 && isStepEnabled('step2')) {
          setGenerating(true);
          const res = await generateDialogue({
            step: 'step2',
            districtId,
            aiDialogueSettingsId: aiDialogueSettingsId ?? undefined,
            facts,
            categoryMaster,
            emotion,
            categoryLabel,
            userObservations: observations,
            questions: buildQuestionsBlock(aiRefs),
          });
          if (res.ok && res.step === 'step2') setLlmStep2(res.result);
          setGenerating(false);
        }
        nextStep = 3;
      } else if (currentStep === 3) {
        if (mode === 'front') {
          // front: Step3 完了 → Step4 (気づきタイトル)、AI で 3 案生成 (old2 L263-267)
          nextStep = 4;
          setLoadingInsightTitles(true);
          const titleRes = await generateTitles({
            districtId,
            postTitles: [observations, diagnosis].filter((s) => s.length > 0),
            emotion,
            categoryLabel,
          });
          if (titleRes.ok && titleRes.titles.length > 0) {
            setInsightTitleSuggestions(titleRes.titles);
          }
          setLoadingInsightTitles(false);
        } else {
          // back: Step3 完了 → Step4 (処方)、AI step3 は既に自動発動済の想定
          nextStep = 4;
        }
      } else if (currentStep === 4) {
        if (mode === 'back') {
          // back: Step4 完了 → Step5 (効能)、AI step4 を取得 (step4 無効ならスキップ、old2 L290)
          if (!llmStep4 && isStepEnabled('step4')) {
            setGenerating(true);
            const res = await generateDialogue({
              step: 'step4',
              districtId,
              aiDialogueSettingsId: aiDialogueSettingsId ?? undefined,
              userObservations: observations,
              userDiagnosis: diagnosis,
              userAction: actionIdea,
              emotion,
              categoryLabel,
            });
            if (res.ok && res.step === 'step4') setLlmStep4(res.result);
            setGenerating(false);
          }
          nextStep = 5;
        }
      } else if (currentStep === 5) {
        // back: Step5 完了 → Step6 (まとめ)、タイトル提案 (old2 L306-312)
        nextStep = 6;
        setLoadingTitles(true);
        const titleRes = await generateTitles({
          districtId,
          postTitles: [observations, actionIdea, expectedEffects].filter((s) => s.length > 0),
          emotion,
          categoryLabel,
        });
        if (titleRes.ok && titleRes.titles.length > 0) setTitleSuggestions(titleRes.titles);
        setLoadingTitles(false);
      }
    } catch (e) {
      console.error('[IdeaWorkspace] handleNextStep error', e);
      setGenerating(false);
      setLoadingInsightTitles(false);
      setLoadingTitles(false);
    }

    setCurrentStep(nextStep);
    // スクロール (旧 L315-325)
    setTimeout(() => {
      const editorEl = document.querySelector('[data-pane="editor"]') as HTMLElement | null;
      const targetEl = document.getElementById(`step-${nextStep}`);
      if (editorEl && targetEl) {
        const topPos = targetEl.offsetTop - 20;
        editorEl.scrollTo({ top: topPos, behavior: 'smooth' });
      } else if (editorEl) {
        editorEl.scrollTo({ top: editorEl.scrollHeight, behavior: 'smooth' });
      }
    }, 150);
  }, [
    canGoNext,
    generating,
    currentStep,
    mode,
    llmStep2,
    llmStep4,
    districtId,
    aiDialogueSettingsId,
    facts,
    categoryMaster,
    emotion,
    categoryLabel,
    observations,
    diagnosis,
    actionIdea,
    expectedEffects,
    aiRefs,
    isStepEnabled,
    generateDialogue,
    generateTitles,
  ]);

  /* ---------- 気づきタイトル再生成 (front Step4、old2 handleGenerateInsightTitles L376-388) ---------- */
  const handleGenerateInsightTitles = useCallback(() => {
    if (!observations.trim() || !diagnosis.trim() || loadingInsightTitles) return;
    setLoadingInsightTitles(true);
    generateTitles({
      districtId,
      postTitles: [observations, diagnosis].filter((s) => s.length > 0),
      emotion,
      categoryLabel,
    })
      .then((res) => {
        if (res.ok && res.titles.length > 0) {
          setInsightTitleSuggestions(res.titles);
        } else {
          window.alert('タイトル案の生成に失敗しました。もう一度お試しください。');
        }
      })
      .catch(() => {
        // 失敗の理由は generateTitles が知らせる (notifyAiError)。
      })
      .finally(() => setLoadingInsightTitles(false));
  }, [
    observations,
    diagnosis,
    loadingInsightTitles,
    districtId,
    emotion,
    categoryLabel,
    generateTitles,
  ]);

  /* ---------- handleSaveInsight (front) ---------- */
  const handleSaveInsight = useCallback(async () => {
    if (saving) return;
    if (!uid) return;
    if (!observations.trim() || !diagnosis.trim() || !insightTitle.trim()) return;
    setSaving(true);
    const sentiment = computeAverageSentiment(selectedPosts);
    const displayName = formatDisplayName({ nickname, ageGroup, gender });
    try {
      await onCreateInsight({
        userId: uid,
        nickname: displayName,
        title: insightTitle.trim(),
        observations: observations.trim(),
        diagnosis: diagnosis.trim(),
        sentiment,
        selectedPostIds: selectedPosts.map((p) => p.id),
        ...(initialThoughts.trim() ? { initialThoughts: initialThoughts.trim() } : {}),
        ...(referenceImageBase64 ? { referenceImageBase64 } : {}),
        llmAnalysis: llmStep1?.analysis ?? '',
        llmResolutionQuestions: llmStep1?.resolutionQuestions ?? [],
        llmDiagnosisQuestions: llmStep2?.diagnosisQuestions ?? [],
        emotionTag: emotion,
        categoryLabel,
      });
      // 楽観 UI (旧 L420-433)
      setSaving(false);
      setSaveMessage('気づきを保存しました ✓');
      setTimeout(() => {
        setSaveMessage('');
        setShowSaveCompleteDialog(true);
      }, 1500);
    } catch (e) {
      console.error('[IdeaWorkspace] handleSaveInsight error', e);
      setSaving(false);
      window.alert('気づきの保存に失敗しました。電波の良い場所でもう一度お試しください。');
    }
  }, [
    saving,
    uid,
    observations,
    diagnosis,
    insightTitle,
    selectedPosts,
    nickname,
    ageGroup,
    gender,
    initialThoughts,
    referenceImageBase64,
    llmStep1,
    llmStep2,
    emotion,
    categoryLabel,
    onCreateInsight,
  ]);

  /* ---------- handleSave (back) ---------- */
  const handleSave = useCallback(async () => {
    if (saving) return;
    if (!uid) return;
    if (!title.trim()) return;
    setSaving(true);
    const finalTitle = ensureUniqueIdeaTitle(title.trim(), allIdeaTitles);
    const displayName = formatDisplayName({ nickname, ageGroup, gender });
    const combinedObs = `【症状・気づき】\n${observations}\n\n【診断】\n${diagnosis}`;
    const combinedAction = `【処方・アクション】\n${actionIdea}\n\n【効能】\n${expectedEffects}`;
    // EcwIdeaReference[] 形式に変換 (旧 v1: AI step3.references をそのまま使う)
    const refsForIdea: EcwIdeaReference[] | undefined = llmStep3?.references;
    try {
      await onCreateIdea({
        userId: uid,
        nickname: displayName,
        title: finalTitle,
        observations: combinedObs,
        actionIdea: combinedAction,
        insightIds: selectedInsights.map((i) => i.id),
        selectedPostIds: selectedPosts.map((p) => p.id),
        ...(refsForIdea && refsForIdea.length > 0 ? { references: refsForIdea } : {}),
        ...(referenceImageBase64 ? { referenceImageBase64 } : {}),
        llmActionQuestions: llmStep3?.actionQuestions ?? [],
        llmEffectQuestions: llmStep4?.effectQuestions ?? [],
        ...(insightSummary ? { llmInsightSummary: insightSummary } : {}),
        emotionTag: emotion,
        emotionDescription:
          ECW_EMOTION_DESCRIPTIONS[emotion as keyof typeof ECW_EMOTION_DESCRIPTIONS] ?? '',
        categoryCode: categoryLabel,
        categoryLabel,
      });
      setSaving(false);
      setSaveMessage('処方箋を保存しました ✓');
      setTimeout(() => {
        setSaveMessage('');
        setShowSaveCompleteDialog(true);
      }, 1500);
    } catch (e) {
      console.error('[IdeaWorkspace] handleSave error', e);
      setSaving(false);
      window.alert('処方箋の保存に失敗しました。電波の良い場所でもう一度お試しください。');
    }
  }, [
    saving,
    uid,
    title,
    allIdeaTitles,
    nickname,
    ageGroup,
    gender,
    observations,
    diagnosis,
    actionIdea,
    expectedEffects,
    selectedInsights,
    selectedPosts,
    llmStep3,
    llmStep4,
    insightSummary,
    referenceImageBase64,
    emotion,
    categoryLabel,
    onCreateIdea,
  ]);

  /* ---------- 右ペイン: 参考事例 → textarea に append (旧 insertTextToEditor 等価) ---------- */
  const appendRefToActionIdea = useCallback((text: string, url?: string) => {
    setActionIdea((prev) => {
      const block = url ? `${text}\n  参考: ${url}` : text;
      return prev ? `${prev}\n・${block}` : `・${block}`;
    });
  }, []);
  const appendRefToEffects = useCallback((text: string, url?: string) => {
    setExpectedEffects((prev) => {
      const block = url ? `${text}\n  参考: ${url}` : text;
      return prev ? `${prev}\n・${block}` : `・${block}`;
    });
  }, []);

  /* ---------- right pane references (step3 後は llmStep3.references で動的差し替え) ----------
   * docs/107 Wave9 / old2 relevantReferences L161-189: step3 後は AI 提案 (imageUrl 付き) を表示。 */
  const rightPaneRefs = useMemo(() => {
    if (mode === 'back' && llmStep3?.references && llmStep3.references.length > 0) {
      return llmStep3.references.map(
        (r, idx) =>
          ({
            id: `llm_ref_${idx}`,
            title: r.title,
            body: r.description ?? '',
            url: r.url,
            imageUrl: r.imageUrl,
            polarity: '✨ AI提案',
          }) as ReturnType<typeof aiReferenceToDisplay>,
      );
    }
    return aiRefs.map(aiReferenceToDisplay);
  }, [mode, llmStep3, aiRefs]);

  /* ---------- 画像 アップロード ---------- */
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setReferenceImageBase64(reader.result as string);
    reader.readAsDataURL(file);
  };

  /* ---------- progress dots (old2 L617-618: front=[1,2,3] / back=[4,5,6]) ----------
   * front の「気づきタイトル」(step4 相当) は old2 同様 progress dot に含めない (Step3 完了後の
   * 仕上げフォーム扱い)。 */
  const progressSteps = mode === 'front' ? [1, 2, 3] : [4, 5, 6];

  /* ============================================================================
   * Render
   * ========================================================================== */
  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* トースト */}
      {saveMessage && (
        <div
          className="fixed top-20 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-8 py-3 rounded-lg font-bold text-base z-[9999] shadow-xl"
          role="status"
        >
          {saveMessage}
        </div>
      )}

      {/* 保存完了ダイアログ */}
      {showSaveCompleteDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-[90%] text-center shadow-2xl">
            <div className="text-4xl mb-3">{mode === 'front' ? '💡' : '💊'}</div>
            <h3 className="mb-2 text-lg text-slate-800 font-bold">
              {mode === 'front' ? '気づきを保存しました！' : '処方箋を保存しました！'}
            </h3>
            <p className="mb-6 text-sm text-slate-600">次にどこへ移動しますか？</p>
            <div className="flex flex-col gap-2.5">
              {mode === 'front' ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSaveCompleteDialog(false);
                      onSavedAndNavigate('insights');
                    }}
                    className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700"
                  >
                    📋 気づき一覧へ
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSaveCompleteDialog(false);
                      onSavedAndNavigate('matrix');
                    }}
                    className="px-6 py-3 bg-sky-600 text-white rounded-lg font-semibold text-sm hover:bg-sky-700"
                  >
                    📊 マトリクス表示へ
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSaveCompleteDialog(false);
                      onSavedAndNavigate('map');
                    }}
                    className="px-6 py-3 bg-emerald-500 text-white rounded-lg font-semibold text-sm hover:bg-emerald-600"
                  >
                    🗺 地図表示へ
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSaveCompleteDialog(false);
                      onSavedAndNavigate('insights');
                    }}
                    className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700"
                  >
                    💡 気づき一覧へ
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSaveCompleteDialog(false);
                      onSavedAndNavigate('ideas');
                    }}
                    className="px-6 py-3 bg-red-500 text-white rounded-lg font-semibold text-sm hover:bg-red-600"
                  >
                    💊 処方箋一覧へ
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ヘッダー */}
      <header className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← {mode === 'back' ? '気づき一覧に戻る' : '分析に戻る'}
        </button>
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <h2 className="font-bold text-sm truncate">
            {mode === 'front'
              ? '処方箋ワークスペース（前半・気づき）'
              : '処方箋ワークスペース（後半・処方）'}
          </h2>
          <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800 border border-amber-200">
            {emotion}
          </span>
          <span className="px-2 py-0.5 rounded-full text-[10px] bg-sky-100 text-sky-800 border border-sky-200">
            {categoryLabel}
          </span>
        </div>
        {/* 「最初からやり直す」 (old2 header L713-723、front mode のみ) */}
        {mode === 'front' && !shouldShowStep0 && (
          <button
            type="button"
            onClick={handleRestart}
            disabled={generating}
            className="px-3 py-1.5 rounded-md text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {generating && !llmStep1 ? 'AIが分析中...' : '🔄 最初からやり直す'}
          </button>
        )}
        {mode === 'front' && currentStep >= 4 && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSaveInsight()}
            disabled={
              !observations.trim() || !diagnosis.trim() || !insightTitle.trim() || saving || !uid
            }
          >
            {saving ? '保存中…' : '💡 気づきとして保存'}
          </Button>
        )}
        {mode === 'back' && currentStep >= 6 && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSave()}
            disabled={!title.trim() || saving || !uid}
          >
            {saving ? '保存中…' : '💊 処方箋を保存'}
          </Button>
        )}
      </header>

      {/* モバイル用トグルバー */}
      <div className="md:hidden flex border-b border-slate-200 bg-white shrink-0">
        <button
          type="button"
          className="flex-1 py-2 text-xs text-slate-700 border-r border-slate-200"
          onClick={() => setShowFactPane(true)}
        >
          {mode === 'front' ? '📋 市民データ' : '💡 気づき'}
        </button>
        <button
          type="button"
          className="flex-1 py-2 text-xs text-slate-700"
          onClick={() => setShowRefPane(true)}
        >
          📚 参考情報
        </button>
      </div>

      {/* 3 ペイン */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[320px_1fr_320px] overflow-hidden">
        {/* === 左ペイン === */}
        <aside
          className={`bg-white border-r border-slate-200 overflow-hidden flex flex-col ${
            showFactPane
              ? 'fixed inset-0 z-50 md:relative md:inset-auto md:z-auto'
              : 'hidden md:flex'
          }`}
        >
          <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2 shrink-0">
            <h3 className="text-sm font-semibold text-slate-700">
              {mode === 'front' ? '📋 市民データ' : '💡 選択した気づき'}
            </h3>
            <button
              type="button"
              onClick={() => setShowFactPane(false)}
              className="md:hidden ml-auto text-xs text-slate-500"
            >
              ✕
            </button>
          </div>
          {/* docs/107 Wave5: 絞込 context 表示 (どの軸で選んだ投稿か)。old2 onIdeaGo filterList 相当。 */}
          {mode === 'front' && filterContext.length > 0 && (
            <div className="px-3 py-2 border-b border-slate-100 bg-sky-50/60 shrink-0">
              <p className="text-[10px] text-slate-500 mb-1">この投稿群の絞込条件:</p>
              <div className="flex flex-wrap gap-1">
                {filterContext.map((f) => (
                  <span
                    key={f}
                    className="px-1.5 py-0.5 rounded-full text-[10px] bg-white border border-sky-200 text-sky-800"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex-1 overflow-auto">
            {mode === 'front' ? (
              <FactPaneFront
                districtId={districtId}
                posts={selectedPosts}
                selectedPost={selectedPost}
                onSelectPost={setSelectedPost}
                miniMapRef={miniMapRef}
              />
            ) : (
              <FactPaneBack
                districtId={districtId}
                insights={selectedInsights}
                posts={selectedPosts}
                selectedInsightDisplay={selectedInsightDisplay}
                onSelectInsight={setSelectedInsightDisplay}
                selectedPost={selectedPost}
                onSelectPost={setSelectedPost}
                miniMapRef={miniMapRef}
              />
            )}
          </div>
        </aside>

        {/* === 中央ペイン === */}
        <section data-pane="editor" className="bg-white overflow-auto px-6 py-4 space-y-6">
          {/* progress dots */}
          <div className="flex items-center gap-2 sticky top-0 bg-white pb-2 border-b border-slate-100 z-10">
            {progressSteps.map((s) => (
              <div
                key={s}
                aria-label={`step ${s}`}
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentStep === s
                    ? 'bg-primary-600'
                    : currentStep > s
                      ? 'bg-emerald-500'
                      : 'bg-slate-300'
                }`}
              />
            ))}
            <span className="ml-2 text-xs text-slate-500">
              Step {Math.min(currentStep, mode === 'front' ? 3 : 6)} / {mode === 'front' ? 3 : 6}
            </span>
          </div>

          {/* STEP 0: 最初の感想 (M4 で step0 有効時のみ、ブロッキング、old2 L934-958) */}
          {shouldShowStep0 && (
            <div id="step-0" className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h4 className="text-amber-700 font-semibold mb-3 text-sm">
                STEP 0: あなたの最初の感想
              </h4>
              <p className="text-sm text-slate-700 mb-3 whitespace-pre-wrap leading-relaxed">
                🤖 {step0Question}
              </p>
              <textarea
                value={initialThoughts}
                onChange={(e) => setInitialThoughts(e.target.value)}
                rows={5}
                placeholder="自由に書いてみてください（空欄のまま進めることもできます）"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
              <div className="mt-3 text-right">
                <Button variant="primary" size="md" onClick={handleStep0Submit}>
                  次へ進む（AIに分析を依頼）→
                </Button>
              </div>
            </div>
          )}

          {/* 初期ローディング (Step1 を生成中、old2 L961-966) */}
          {!shouldShowStep0 && mode === 'front' && generating && !llmStep1 && (
            <div className="text-center py-10 text-slate-500">
              <div className="text-2xl mb-3 animate-spin inline-block">🌀</div>
              <p className="text-sm">AIが市民データを分析しています...</p>
            </div>
          )}

          {/* Step 0 で記入した「最初の感想」を読み返せるように表示 (old2 L986-994) */}
          {!shouldShowStep0 && mode === 'front' && initialThoughts.trim().length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h4 className="text-amber-700 font-semibold mb-2 text-sm">
                ✍️ STEP 0: あなたの最初の感想
              </h4>
              <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap p-2 bg-white rounded border border-amber-300">
                {initialThoughts}
              </div>
            </div>
          )}

          {/* back モード: Insight summary */}
          {mode === 'back' && (
            <div id="step-summary" className="rounded-lg border border-sky-200 bg-sky-50 p-4">
              <h4 className="text-sky-700 font-semibold mb-1 text-sm">📋 選択した気づきの概要</h4>
              {insightSummary ? (
                <p className="text-sm text-slate-700 leading-relaxed">{insightSummary}</p>
              ) : (
                <p className="text-xs text-slate-500">AIが概要を準備中です…</p>
              )}
              <div className="mt-2 text-[11px] text-slate-500">
                （{selectedInsights.length} 件の気づきをベースに検討します）
              </div>
            </div>
          )}

          {/* 中間ローディング (old2 L1028-1034): 初期 step1 ローディングとは別文言。
              front の初期 step1 生成中 (llmStep1 未取得) は上の初期ローディングで表示済のため除外。 */}
          {generating && !(mode === 'front' && !llmStep1) && (
            <div className="text-center py-5 text-slate-500 border-t border-dashed border-slate-200">
              <div className="text-2xl mb-2 animate-spin inline-block">🌀</div>
              <p className="text-sm">あなたの回答をもとにAIが次の質問を考えています...</p>
            </div>
          )}

          {/* front の step 群 / nav は Step0 ブロッキング中は非表示 (old2 L968) */}
          {!shouldShowStep0 && (
            <>
              {/* Step 1 — AI 所見 (front) */}
              {mode === 'front' && currentStep >= 1 && (
                <StepPane id="step-1" title="STEP 1: AIからの所見" accentClass="border-l-cyan-500">
                  {llmStep1?.analysis ? (
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {llmStep1.analysis}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500">まだ分析が完了していません。</p>
                  )}
                </StepPane>
              )}

              {/* Step 2 — 症状 (front) */}
              {mode === 'front' && currentStep >= 2 && (
                <StepPane
                  id="step-2"
                  title="STEP 2: いま何が起きている？（症状）"
                  accentClass="border-l-yellow-500"
                >
                  <strong className="text-sm text-slate-700">🤖 一緒に考えてみましょう：</strong>
                  <ul className="list-disc pl-5 mt-1 mb-3 text-sm text-slate-700 space-y-0.5">
                    {(llmStep1?.resolutionQuestions ?? []).map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                    {(!llmStep1?.resolutionQuestions ||
                      llmStep1.resolutionQuestions.length === 0) && (
                      <li>この場所で具体的に何が起きていますか？</li>
                    )}
                  </ul>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    症状 (いま感じていることを自由に書いてください)
                  </label>
                  <textarea
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                    rows={5}
                    disabled={currentStep > 2}
                    placeholder="感じたこと、気になったことを自由に書いてみてください"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm disabled:bg-slate-50"
                  />
                </StepPane>
              )}

              {/* Step 3 — 診断 (front) */}
              {mode === 'front' && currentStep >= 3 && (
                <StepPane
                  id="step-3"
                  title="STEP 3: なぜそうなっている？（診断）"
                  accentClass="border-l-red-500"
                >
                  <strong className="text-sm text-slate-700">
                    🤖 もう少し深掘りしてみましょう：
                  </strong>
                  <ul className="list-disc pl-5 mt-1 mb-3 text-sm text-slate-700 space-y-0.5">
                    {(llmStep2?.diagnosisQuestions ?? []).map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                    {(!llmStep2?.diagnosisQuestions ||
                      llmStep2.diagnosisQuestions.length === 0) && (
                      <li>その背景にある原因は何だと思いますか？</li>
                    )}
                  </ul>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    診断 (なぜそうなっているか、考えを書いてください)
                  </label>
                  <textarea
                    value={diagnosis}
                    onChange={(e) => setDiagnosis(e.target.value)}
                    rows={5}
                    disabled={currentStep > 3}
                    placeholder="原因や背景について、あなたの考えを書いてみてください"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm disabled:bg-slate-50"
                  />
                </StepPane>
              )}

              {/* Step 4 (front) — 気づきタイトル */}
              {mode === 'front' && currentStep >= 4 && (
                <StepPane
                  id="step-4"
                  title="気づきのタイトルとキャッチ画像"
                  accentClass="border-l-emerald-500"
                >
                  <p className="text-xs text-slate-600 mb-2">
                    この気づきにタイトルとキャッチ画像をつけて保存しましょう！
                  </p>
                  {/* AIおすすめタイトル + 再生成ボタン (old2 L1060-1091) */}
                  <div className="mb-2">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-semibold text-slate-600">
                        🤖 AIおすすめタイトル
                      </span>
                      <button
                        type="button"
                        onClick={handleGenerateInsightTitles}
                        disabled={loadingInsightTitles || !observations.trim() || !diagnosis.trim()}
                        className="px-2.5 py-0.5 text-[11px] bg-sky-50 border border-sky-300 rounded-full text-sky-800 font-semibold hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingInsightTitles ? '考え中...' : '💡 タイトル案を生成'}
                      </button>
                    </div>
                    {loadingInsightTitles && insightTitleSuggestions.length === 0 ? (
                      <p className="text-[11px] text-slate-500">タイトル案を考え中...</p>
                    ) : insightTitleSuggestions.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {insightTitleSuggestions.map((t, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setInsightTitle(t)}
                            className="px-3 py-1 text-xs bg-emerald-50 border border-emerald-300 rounded-full text-emerald-800 hover:bg-emerald-100"
                          >
                            💡 {t}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-400">
                        （「タイトル案を生成」でAIに提案してもらえます）
                      </p>
                    )}
                  </div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    気づきのタイトル <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={insightTitle}
                    onChange={(e) => setInsightTitle(e.target.value)}
                    placeholder="（例）駅前広場が足りない商店街"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm mb-3"
                  />
                  {/* キャッチ画像 (キービジュアル) — 処方箋(Step6)と同じピッカーを気づきにも提供。 */}
                  <CatchImagePicker
                    selectedPosts={selectedPosts}
                    value={referenceImageBase64}
                    onChange={setReferenceImageBase64}
                    fileInputRef={fileInputRef}
                    onFileUpload={handleFileUpload}
                    onCaptureMap={() => void handleCaptureMap()}
                    capturing={capturingMap}
                  />
                </StepPane>
              )}

              {/* Step 4 (back) — 処方 */}
              {mode === 'back' && currentStep >= 4 && (
                <StepPane
                  id="step-4"
                  title="STEP 4: どうしたらいい？（処方）"
                  accentClass="border-l-emerald-500"
                >
                  <strong className="text-sm text-slate-700">
                    🤖 アイデアを出してみましょう：
                  </strong>
                  <ul className="list-disc pl-5 mt-1 mb-3 text-sm text-slate-700 space-y-0.5">
                    {(llmStep3?.actionQuestions ?? []).map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                    {(!llmStep3?.actionQuestions || llmStep3.actionQuestions.length === 0) && (
                      <li>ここに対して、どんなアプローチが考えられますか？</li>
                    )}
                  </ul>
                  <p className="text-[11px] text-slate-500 mb-2">
                    ※ 右のパネルに参考事例があります。ヒントにしてみてください！
                  </p>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    処方 (具体的なアクションプラン)
                  </label>
                  <textarea
                    value={actionIdea}
                    onChange={(e) => setActionIdea(e.target.value)}
                    rows={5}
                    disabled={currentStep > 4}
                    placeholder="具体的なアイデアやアクションを書いてみてください"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm disabled:bg-slate-50"
                  />
                </StepPane>
              )}

              {/* Step 5 (back) — 効能 */}
              {mode === 'back' && currentStep >= 5 && (
                <StepPane
                  id="step-5"
                  title="STEP 5: どんな未来が見える？（効能）"
                  accentClass="border-l-purple-500"
                >
                  <strong className="text-sm text-slate-700">
                    🤖 未来をイメージしてみましょう：
                  </strong>
                  <ul className="list-disc pl-5 mt-1 mb-3 text-sm text-slate-700 space-y-0.5">
                    {(llmStep4?.effectQuestions ?? []).map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                    {(!llmStep4?.effectQuestions || llmStep4.effectQuestions.length === 0) && (
                      <li>これが実現したら、この街はどうなりそうですか？</li>
                    )}
                  </ul>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    効能 (期待される変化)
                  </label>
                  <textarea
                    value={expectedEffects}
                    onChange={(e) => setExpectedEffects(e.target.value)}
                    rows={4}
                    disabled={currentStep > 5}
                    placeholder="実現したらどんな良い変化が起きるか、想像してみてください"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm disabled:bg-slate-50"
                  />
                </StepPane>
              )}

              {/* Step 6 (back) — まとめ */}
              {mode === 'back' && currentStep >= 6 && (
                <StepPane
                  id="step-6"
                  title="STEP 6: まとめ（処方箋の仕上げ）"
                  accentClass="border-l-cyan-500"
                >
                  <p className="text-xs text-slate-600 mb-2">
                    印象的なタイトルとキャッチ画像を設定して、処方箋を完成させましょう！
                  </p>
                  {/* AIおすすめタイトル: loading / 候補 / empty (old2 L1143-1169) */}
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-slate-600 mb-1">
                      🤖 AIおすすめタイトル
                    </div>
                    {loadingTitles ? (
                      <p className="text-[11px] text-slate-500">タイトル案を考え中...</p>
                    ) : titleSuggestions.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {titleSuggestions.map((t, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setTitle(t)}
                            className="px-3 py-1 text-xs bg-sky-50 border border-sky-300 rounded-full text-sky-800 hover:bg-sky-100"
                          >
                            💡 {t}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-400">（タイトル案なし）</p>
                    )}
                  </div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    処方箋タイトル <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="（例）居心地の良いストリートテラス化計画"
                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm mb-3"
                  />

                  {/* キャッチ画像 (キービジュアル) — 気づき/処方箋で共有 (old2 IdeaForm STEP6)。 */}
                  <CatchImagePicker
                    selectedPosts={selectedPosts}
                    value={referenceImageBase64}
                    onChange={setReferenceImageBase64}
                    fileInputRef={fileInputRef}
                    onFileUpload={handleFileUpload}
                    onCaptureMap={() => void handleCaptureMap()}
                    capturing={capturingMap}
                  />
                </StepPane>
              )}

              {/* ナビゲーション */}
              <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-6 px-6 py-3 flex justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentStep((p) => Math.max(startStep, p - 1))}
                  disabled={currentStep === startStep}
                  className="px-4 py-2 text-sm border border-slate-300 rounded bg-white text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                >
                  ← 前へ戻って修正
                </button>

                {mode === 'front' && currentStep < 4 && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => void handleNextStep()}
                    disabled={!canGoNext() || generating}
                  >
                    次へ進む →
                  </Button>
                )}

                {mode === 'front' && currentStep >= 4 && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => void handleSaveInsight()}
                    disabled={
                      !observations.trim() ||
                      !diagnosis.trim() ||
                      !insightTitle.trim() ||
                      saving ||
                      !uid
                    }
                  >
                    {saving ? '保存中…' : '💡 気づきとして保存'}
                  </Button>
                )}

                {mode === 'back' && currentStep < 6 && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => void handleNextStep()}
                    disabled={!canGoNext() || generating}
                  >
                    次へ進む →
                  </Button>
                )}

                {mode === 'back' && currentStep >= 6 && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => void handleSave()}
                    disabled={!title.trim() || saving || !uid}
                  >
                    {saving ? '保存中…' : '💊 処方箋を保存'}
                  </Button>
                )}
              </div>
            </>
          )}
        </section>

        {/* === 右ペイン === */}
        <aside
          className={`bg-white border-l border-slate-200 overflow-hidden flex flex-col ${
            showRefPane
              ? 'fixed inset-0 z-50 md:relative md:inset-auto md:z-auto'
              : 'hidden md:flex'
          }`}
        >
          <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2 shrink-0">
            <h3 className="text-sm font-semibold text-slate-700">📚 参考情報・事例</h3>
            <button
              type="button"
              onClick={() => setShowRefPane(false)}
              className="md:hidden ml-auto text-xs text-slate-500"
            >
              ✕
            </button>
          </div>
          <div className="flex-1 overflow-auto p-3 space-y-3">
            {refsState.status === 'loading' && <p className="text-xs text-slate-500">読込中…</p>}
            {/* docs/107 Wave9: ReferenceGrid 二重描画解消 — step3 references は rightPaneRefs に
                合流済 (imageUrl 付き) のため、別途 ReferenceGrid を描かない。 */}
            {rightPaneRefs.length === 0 && refsState.status === 'ready' && (
              <p className="text-xs text-slate-500">関連する参考情報は見つかりませんでした。</p>
            )}
            {rightPaneRefs.map((r) => (
              <article
                key={r.id}
                className="border border-slate-200 rounded bg-slate-50 text-xs overflow-hidden"
              >
                {/* アイキャッチ画像バナー (old2 L1322-1329)、壊れ画像は非表示 */}
                {r.imageUrl && <RefImageBanner src={r.imageUrl} />}
                <div className="p-2">
                  {r.polarity && (
                    <div className="mb-1">
                      <span className="px-1.5 py-0.5 rounded bg-white border border-slate-300 text-[10px] text-slate-700">
                        {r.polarity}
                      </span>
                    </div>
                  )}
                  <div className="font-semibold text-slate-800 mb-1 break-words">{r.title}</div>
                  {r.body && (
                    <p className="text-[11px] text-slate-600 leading-relaxed mb-1">{r.body}</p>
                  )}
                  {/* 参考 URL は AI / 利用者由来。http(s) 以外はリンクにしない (docs/13 §7) */}
                  {safeHttpUrl(r.url) && (
                    <a
                      href={safeHttpUrl(r.url) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-sky-700 underline"
                    >
                      🔗 参考サイト →
                    </a>
                  )}
                  {mode === 'back' && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      <button
                        type="button"
                        onClick={() => appendRefToActionIdea(r.title, r.url)}
                        className="px-2 py-0.5 text-[10px] bg-emerald-50 border border-emerald-300 rounded text-emerald-800 hover:bg-emerald-100"
                      >
                        → 処方に追加
                      </button>
                      <button
                        type="button"
                        onClick={() => appendRefToEffects(r.title, r.url)}
                        className="px-2 py-0.5 text-[10px] bg-purple-50 border border-purple-300 rounded text-purple-800 hover:bg-purple-100"
                      >
                        → 効能に追加
                      </button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </aside>
      </div>

      {/* 市民データの写真タップ → 投稿アプリと同じ投稿詳細を overlay 表示 (front/back 共通)。
          旧 PostDetailInline (最小表示) を廃し、実 PostDetailModal で同一内容を出す。 */}
      <PostDetailModal
        open={selectedPost !== null}
        onClose={() => setSelectedPost(null)}
        post={selectedPost}
        districtId={districtId}
        currentUid={uid}
        selfUids={selfUids}
        analysisDisplayConfigId={analysisDisplayConfigId}
      />
    </div>
  );
}

/* ============================================================================
 * Sub-components
 * ========================================================================== */

function StepPane({
  id,
  title,
  accentClass,
  children,
}: {
  id: string;
  title: string;
  accentClass: string;
  children: React.ReactNode;
}) {
  return (
    <div
      id={id}
      className={`rounded-lg border border-slate-200 bg-white p-4 border-l-4 ${accentClass}`}
    >
      <h4 className="text-sm font-semibold text-cyan-700 mb-3">{title}</h4>
      {children}
    </div>
  );
}

/**
 * 右ペイン参考事例のアイキャッチ画像バナー (old2 L1322-1329)。
 * 壊れ画像は state で非表示にする (DOM 操作でなく React 流に)。
 */
function RefImageBanner({ src }: { src: string }) {
  const [err, setErr] = useState(false);
  if (err) return null;
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setErr(true)}
      className="w-full h-28 object-cover"
    />
  );
}

/**
 * 投稿写真サムネイル (usePhotoUrl で実写真を解決、old2 fact-img / DUMMY_IMAGE_URL 相当)。
 * 写真が無い / 解決前は 📷 プレースホルダ。
 */
function PostThumb({ post, className }: { post: PostMinimal; className?: string }) {
  const photo = usePhotoUrl(post.photoThumbPath, post.photoThumbUrl ?? post.photoUrl);
  if (photo.status === 'ready' && photo.url) {
    return (
      <img
        src={photo.url}
        alt={post.title || '投稿写真'}
        loading="lazy"
        className={`object-cover bg-slate-100 ${className ?? ''}`}
      />
    );
  }
  return (
    <div
      className={`bg-slate-100 flex items-center justify-center text-slate-400 ${
        photo.status === 'loading' ? 'animate-pulse' : ''
      } ${className ?? ''}`}
      aria-hidden="true"
    >
      📷
    </div>
  );
}

/**
 * キャッチ画像 (キービジュアル) ピッカー (old2 IdeaForm STEP6 L1170-1240)。
 *
 * 気づき (front Step4) / 処方箋 (back Step6) の両方で共有する。3 ソース:
 *   - 投稿写真から選択 (CatchImageThumb、実 URL を value に格納)
 *   - 独自アップロード (FileReader → dataURL)
 *   - 地図をキャプチャ (左ペイン mini-map → PNG dataURL)
 *
 * old2 では処方箋のみだったが、本機能で気づきにも拡張 (docs/107 後続)。UI は old2 STEP6 を逐語踏襲。
 */
function CatchImagePicker({
  selectedPosts,
  value,
  onChange,
  fileInputRef,
  onFileUpload,
  onCaptureMap,
  capturing,
}: {
  selectedPosts: PostMinimal[];
  value: string;
  onChange: (url: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCaptureMap: () => void;
  capturing: boolean;
}) {
  return (
    <>
      <label className="block text-xs font-semibold text-slate-700 mb-1">キャッチ画像</label>
      {selectedPosts.length > 0 && (
        <>
          <p className="text-[11px] text-slate-500 mb-1">投稿写真から選択:</p>
          {/* old2 L1178-1199: 投稿写真の実画像から選択。選択は実 URL を referenceImage に格納。 */}
          <div className="flex flex-wrap gap-1 mb-2">
            {selectedPosts.map((p) => {
              if (!p.photoThumbPath && !p.photoPath) return null;
              return (
                <CatchImageThumb
                  key={p.id}
                  post={p}
                  selectedUrl={value}
                  onSelect={onChange}
                  onClear={() => onChange('')}
                />
              );
            })}
          </div>
        </>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          className="px-3 py-1.5 text-xs bg-slate-100 border border-slate-300 rounded hover:bg-slate-200"
          onClick={() => fileInputRef.current?.click()}
        >
          📤 独自アップロード
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFileUpload}
        />
        {/* 地図キャプチャ (旧 IdeaForm L1204-1237、map.getCanvas().toDataURL)。 */}
        <button
          type="button"
          onClick={onCaptureMap}
          disabled={capturing}
          className="px-3 py-1.5 text-xs bg-sky-50 border border-sky-300 rounded text-sky-800 hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="左ペインの地図を画像化してキャッチに使う"
        >
          {capturing ? '🗺 キャプチャ中…' : '🗺 地図をキャプチャ'}
        </button>
      </div>
      {value && (
        <div className="mt-2">
          <img src={value} alt="キャッチ" className="max-w-full rounded border border-slate-300" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="mt-1 text-xs text-slate-500 hover:text-red-600"
          >
            画像をクリア
          </button>
        </div>
      )}
    </>
  );
}

/**
 * Step6 キャッチ画像ピッカーのサムネ (old2 L1180-1198)。
 * 投稿写真の実 URL を usePhotoUrl で解決し、選択時はその URL を referenceImage に格納する。
 * 写真未解決の間は 📷 プレースホルダ (URL 不在のため選択不可)。
 */
function CatchImageThumb({
  post,
  selectedUrl,
  onSelect,
  onClear,
}: {
  post: PostMinimal;
  selectedUrl: string;
  onSelect: (url: string) => void;
  onClear: () => void;
}) {
  const photo = usePhotoUrl(
    post.photoThumbPath ?? post.photoPath,
    post.photoThumbUrl ?? post.photoUrl,
  );
  const url = photo.status === 'ready' ? photo.url : undefined;
  const isSelected = !!url && selectedUrl === url;
  return (
    <div className="relative">
      <button
        type="button"
        disabled={!url}
        className={`w-16 h-12 rounded overflow-hidden border flex items-center justify-center bg-slate-100 ${
          isSelected ? 'border-cyan-500 border-[3px]' : 'border-slate-300 opacity-60'
        } disabled:cursor-not-allowed`}
        onClick={() => url && onSelect(url)}
        aria-label={`画像を選択: ${post.title}`}
      >
        {url ? (
          <img src={url} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <span className="text-slate-400 text-xs">📷</span>
        )}
      </button>
      {isSelected && (
        <button
          type="button"
          onClick={onClear}
          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-[10px] flex items-center justify-center"
          aria-label="選択解除"
        >
          ✕
        </button>
      )}
    </div>
  );
}

// 旧 PostDetailInline (最小インライン詳細) は撤去。投稿詳細は実 PostDetailModal を overlay 表示し、
// 投稿アプリと同一内容 (入力項目 / AI 解析 / 同感 / 編集削除 等) を出す。

function FactPaneFront({
  districtId,
  posts,
  selectedPost,
  onSelectPost,
  miniMapRef,
}: {
  districtId: string;
  posts: PostMinimal[];
  selectedPost: PostMinimal | null;
  onSelectPost: (p: PostMinimal | null) => void;
  miniMapRef: React.RefObject<IdeaWorkspaceMiniMapHandle>;
}) {
  // 写真タップは投稿詳細 (PostDetailModal overlay) を開く。一覧/地図は出したままにする。
  return (
    <div className="p-3 space-y-2">
      {/* Phase B+1.1: MapLibre mini-map (front mode、番号付きマーカー、旧 IdeaForm L799 + L535-551) */}
      <IdeaWorkspaceMiniMap
        ref={miniMapRef}
        districtId={districtId}
        mode="front"
        selectedPosts={posts}
        selectedPost={selectedPost}
        onSelectPost={(p) => onSelectPost(p)}
        className="w-full h-40 rounded border border-slate-200 overflow-hidden bg-slate-100"
      />
      {/* old2 fact-list L801-824: 番号 + タイトル + 実写真 + 気づき。カード全体クリックで詳細。 */}
      <ul className="space-y-2">
        {posts.map((p, idx) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onSelectPost(p)}
              className="w-full text-left rounded border border-slate-200 bg-white hover:bg-slate-50 p-2"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-[11px] font-bold flex items-center justify-center shrink-0">
                  {idx + 1}
                </span>
                <strong className="text-sm text-slate-800 truncate">{p.title}</strong>
              </div>
              <PostThumb post={p} className="w-full h-28 rounded mb-1.5" />
              {p.comment && (
                <p className="text-[11px] text-slate-600 line-clamp-2">
                  <strong>気づき：</strong>
                  {p.comment}
                </p>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FactPaneBack({
  districtId,
  insights,
  posts,
  selectedInsightDisplay,
  onSelectInsight,
  selectedPost,
  onSelectPost,
  miniMapRef,
}: {
  districtId: string;
  insights: InsightMinimal[];
  posts: PostMinimal[];
  selectedInsightDisplay: InsightMinimal | null;
  onSelectInsight: (i: InsightMinimal | null) => void;
  selectedPost: PostMinimal | null;
  onSelectPost: (p: PostMinimal | null) => void;
  miniMapRef: React.RefObject<IdeaWorkspaceMiniMapHandle>;
}) {
  if (selectedInsightDisplay) {
    return (
      <div className="p-3">
        <button
          type="button"
          onClick={() => {
            onSelectInsight(null);
            onSelectPost(null);
          }}
          className="mb-3 px-3 py-1.5 text-xs bg-slate-100 border border-slate-300 rounded text-slate-700 hover:bg-slate-200"
        >
          ← 選択した気づき一覧に戻る
        </button>
        <h4
          className="text-sm font-bold mb-2 text-slate-800 pb-1 border-b-2"
          style={{ borderColor: sentimentToHsl(selectedInsightDisplay.sentiment) }}
        >
          {selectedInsightDisplay.title}
        </h4>
        <div className="mb-3">
          <div className="text-[11px] text-slate-500 font-semibold mb-0.5">
            症状（起きていること）
          </div>
          <p className="text-xs text-slate-800 whitespace-pre-wrap">
            {selectedInsightDisplay.observations}
          </p>
        </div>
        <div className="mb-3">
          <div className="text-[11px] text-slate-500 font-semibold mb-0.5">
            診断（その理由・背景）
          </div>
          <p className="text-xs text-slate-800 whitespace-pre-wrap">
            {selectedInsightDisplay.diagnosis}
          </p>
        </div>
        <div className="mb-3">
          <div className="text-[11px] text-slate-500 font-semibold mb-1">
            ベースとなった市民データ
          </div>
          <ul className="grid grid-cols-3 gap-1">
            {selectedInsightDisplay.selectedPostIds.map((pid) => {
              const post = posts.find((p) => p.id === pid);
              if (!post) return null;
              return (
                <li key={pid}>
                  <button
                    type="button"
                    onClick={() => onSelectPost(post)}
                    className="w-full rounded border border-slate-200 overflow-hidden hover:ring-2 hover:ring-primary-400 focus:outline-none"
                    title={post.title}
                  >
                    <PostThumb post={post} className="w-full aspect-square" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        {/* 投稿写真タップは投稿詳細 (PostDetailModal overlay) を開く (root で表示)。 */}
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2">
      {/* Phase B+1.1: MapLibre mini-map (back mode、insight ごとに 8 色のマーカー、旧 L552-583) */}
      <IdeaWorkspaceMiniMap
        ref={miniMapRef}
        districtId={districtId}
        mode="back"
        selectedPosts={posts}
        selectedPost={selectedPost}
        selectedInsights={insights}
        // この分岐は早期 return で `selectedInsightDisplay` が null に narrow されているため、
        // mini-map 上で active highlight する insight は存在しない (null 固定)。
        selectedInsightId={null}
        onSelectPost={(p) => onSelectPost(p)}
        onSelectInsight={(i) => onSelectInsight(i)}
        className="w-full h-40 rounded border border-slate-200 overflow-hidden bg-slate-100"
      />
      <ul className="space-y-2">
        {insights.map((insight, idx) => {
          const color = COLOR_PALETTE[idx % COLOR_PALETTE.length];
          return (
            <li key={insight.id}>
              <button
                type="button"
                onClick={() => onSelectInsight(insight)}
                className="w-full text-left rounded border border-slate-200 bg-white hover:bg-slate-50 p-2"
                style={{ borderLeftWidth: 6, borderLeftColor: color }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center"
                    style={{ backgroundColor: color }}
                  >
                    {idx + 1}
                  </span>
                  <strong className="text-sm text-slate-800 truncate">{insight.title}</strong>
                </div>
                <p className="text-[11px] text-slate-600 line-clamp-2">{insight.observations}</p>
                {/* 写真サムネ strip (old2 L901-908): 紐づく投稿の実写真を横並び。 */}
                {insight.selectedPostIds.length > 0 && (
                  <div className="flex gap-1 mt-2 overflow-x-auto pb-0.5">
                    {insight.selectedPostIds.map((pid) => {
                      const p = posts.find((post) => post.id === pid);
                      if (!p) return null;
                      return (
                        <PostThumb key={pid} post={p} className="w-10 h-10 rounded shrink-0" />
                      );
                    })}
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
